/**
 * core · Entidades DV e grupos de consolidação (spec 03 §2 · spec 02 §1.1 e §1.4 · tela 10.2).
 */
import { z } from 'zod'
import { codigoDeMoeda, codigoDePais, dataNominal, uuid } from './comum.js'

export const ambienteDaEntidade = z.enum(['producao', 'sandbox'])
export type AmbienteDaEntidade = z.infer<typeof ambienteDaEntidade>

/**
 * Endereço estruturado — os oito campos do GLOSSARIO, nesta ordem.
 *
 * Estruturado e não texto livre porque o endereço fiscal aparece na NF: um
 * logradouro digitado em campo único não dá para separar depois, e a prefeitura
 * pede os pedaços.
 */
export const endereco = z.object({
  cep: z.string().optional(),
  logradouro: z.string().optional(),
  numero: z.string().optional(),
  complemento: z.string().optional(),
  bairro: z.string().optional(),
  cidade: z.string().optional(),
  uf: z.string().optional(),
  pais: z.string().optional(),
})
export type Endereco = z.infer<typeof endereco>

export const entidade = z.object({
  id: uuid,
  nome: z.string(),
  razaoSocial: z.string().nullable(),
  pais: codigoDePais,
  documento: z.string().nullable(),
  moedaFuncional: codigoDeMoeda,
  regimeFiscalId: uuid.nullable(),
  regimeFiscalNome: z.string().nullable(),
  provedorNfseId: uuid.nullable(),
  provedorNfseNome: z.string().nullable(),
  municipioEmissor: z.string().nullable(),
  inscricaoMunicipal: z.string().nullable(),
  inscricaoEstadual: z.string().nullable(),
  enderecoFiscal: endereco.nullable(),
  certificadoValidade: dataNominal.nullable(),
  ativa: z.boolean(),
  ambiente: ambienteDaEntidade,
  /** Contagem, não valor: a tela mostra "N contas" e o CRUD delas vive em 6.2. */
  contas: z.number().int(),
  gruposConsolidacao: z.array(z.object({ id: uuid, nome: z.string() })),
})
export type Entidade = z.infer<typeof entidade>

/**
 * Nova entidade: só o essencial.
 *
 * `ativa` NÃO entra aqui — entidade nasce "Prevista" (spec 04-TELAS/10.2), e a
 * ativação é um ato próprio com regra de negócio. Aceitar `ativa: true` na criação
 * daria a volta na regra pela porta dos fundos.
 */
export const entradaDeNovaEntidade = z.object({
  nome: z.string().min(1, 'Informe o nome.'),
  razaoSocial: z.string().nullish(),
  pais: codigoDePais,
  moedaFuncional: codigoDeMoeda,
  documento: z.string().nullish(),
})
export type EntradaDeNovaEntidade = z.infer<typeof entradaDeNovaEntidade>

export const entradaDeEdicaoDeEntidade = z.object({
  nome: z.string().min(1, 'Informe o nome.').optional(),
  razaoSocial: z.string().nullish(),
  pais: codigoDePais.optional(),
  documento: z.string().nullish(),
  moedaFuncional: codigoDeMoeda.optional(),
  regimeFiscalId: uuid.nullish(),
  provedorNfseId: uuid.nullish(),
  municipioEmissor: z.string().nullish(),
  inscricaoMunicipal: z.string().nullish(),
  inscricaoEstadual: z.string().nullish(),
  enderecoFiscal: endereco.nullish(),
  certificadoValidade: dataNominal.nullish(),
  ativa: z.boolean().optional(),
})
export type EntradaDeEdicaoDeEntidade = z.infer<typeof entradaDeEdicaoDeEntidade>

/**
 * D-179, DS-625: o regime fiscal muda só por este ato, com trilha. `mudanca` é a troca de regime que passa a
 * valer a partir de uma competência; `correcao` declara que o valor anterior foi engano e nunca vigorou (a linha
 * antiga da auditoria fica, e o ato a cita). A edição genérica da entidade não troca o regime.
 */
export const tipoDoAtoDeRegime = z.enum(['mudanca', 'correcao'])
export type TipoDoAtoDeRegime = z.infer<typeof tipoDoAtoDeRegime>

export const entradaDoAtoDeRegime = z.object({
  regimeFiscalId: uuid,
  tipo: tipoDoAtoDeRegime,
  aPartirDe: dataNominal.refine((data) => data.endsWith('-01'), 'O regime vale a partir do primeiro dia de um mês.'),
  fonte: z.string().trim().min(1, 'Informe a fonte.').max(300),
  motivo: z.string().trim().min(1, 'Informe o motivo.').max(500),
  /** O id da linha de auditoria que a correção explica (a trilha numera por inteiro). */
  corrigeAuditoriaId: z.string().regex(/^\d{1,19}$/, 'Informe o número da linha de auditoria.').nullable().optional(),
})
export type EntradaDoAtoDeRegime = z.infer<typeof entradaDoAtoDeRegime>

/** Uma linha do histórico do regime, lida da trilha de domínio. */
export const atoDeRegime = z.object({
  quando: z.string(),
  atorNome: z.string().nullable(),
  de: z.string().nullable(),
  para: z.string().nullable(),
  tipo: tipoDoAtoDeRegime,
  aPartirDe: dataNominal,
  fonte: z.string(),
  motivo: z.string(),
})
export type AtoDeRegime = z.infer<typeof atoDeRegime>

/** O contexto do seletor do cabeçalho (DS-40): só entidades ATIVAS + os grupos. */
export const contextoDeEntidades = z.object({
  entidades: z.array(z.object({
    id: uuid,
    nome: z.string(),
    moedaFuncional: codigoDeMoeda,
    ambiente: ambienteDaEntidade,
  })),
  grupos: z.array(z.object({ id: uuid, nome: z.string(), moedaReporte: codigoDeMoeda })),
})
export type ContextoDeEntidades = z.infer<typeof contextoDeEntidades>

export const grupoDeConsolidacao = z.object({
  id: uuid,
  nome: z.string(),
  moedaReporte: codigoDeMoeda,
  entidades: z.array(z.object({ id: uuid, nome: z.string() })),
})
export type GrupoDeConsolidacao = z.infer<typeof grupoDeConsolidacao>

export const entradaDeGrupoDeConsolidacao = z.object({
  nome: z.string().min(1, 'Informe o nome.'),
  moedaReporte: codigoDeMoeda.default('BRL'),
  entidadeIds: z.array(uuid),
})
export type EntradaDeGrupoDeConsolidacao = z.infer<typeof entradaDeGrupoDeConsolidacao>
